import { useEffect } from "react";

const SITE_NAME = "Paras Organics";
const DEFAULT_ORIGIN = "https://parasorganics.com";
export const DEFAULT_SEO_KEYWORDS = [
  "Paras Organics",
  "organic vermicompost",
  "vermicompost fertilizer",
  "vermiwash",
  "organic fertilizer",
  "chemical free fertilizer",
  "FCO certified vermicompost",
  "soil health",
  "plant growth fertilizer",
  "home gardening",
  "organic farming",
  "terrace garden fertilizer",
  "landscaping fertilizer",
  "nursery plant care",
  "Gujarat organic fertilizer"
];

function getAbsoluteUrl(pathOrUrl) {
  if (!pathOrUrl) return DEFAULT_ORIGIN;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${DEFAULT_ORIGIN}${pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`}`;
}

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      element.setAttribute(key, value);
    }
  });
}

function upsertLink(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      element.setAttribute(key, value);
    }
  });
}

export default function SEOHead({
  title,
  description,
  path,
  image,
  keywords = DEFAULT_SEO_KEYWORDS,
  type = "website",
  publishedTime,
  modifiedTime,
  schema = []
}) {
  useEffect(() => {
    const pageTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    const canonical = getAbsoluteUrl(path || window.location.pathname);
    const ogImage = getAbsoluteUrl(image || "/favicon.png");
    const schemaItems = Array.isArray(schema) ? schema : [schema];

    document.title = pageTitle;
    upsertMeta('meta[name="description"]', { name: "description", content: description });
    upsertMeta('meta[name="keywords"]', { name: "keywords", content: Array.isArray(keywords) ? keywords.join(", ") : keywords });
    upsertMeta('meta[name="robots"]', { name: "robots", content: "index, follow, max-image-preview:large" });
    upsertLink('link[rel="canonical"]', { rel: "canonical", href: canonical });

    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: SITE_NAME });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: type });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: pageTitle });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: ogImage });

    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: pageTitle });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: ogImage });

    if (publishedTime) {
      upsertMeta('meta[property="article:published_time"]', { property: "article:published_time", content: publishedTime });
    }

    if (modifiedTime) {
      upsertMeta('meta[property="article:modified_time"]', { property: "article:modified_time", content: modifiedTime });
    }

    document.head.querySelectorAll('script[data-managed-seo="true"]').forEach((node) => node.remove());
    schemaItems.filter(Boolean).forEach((item) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.managedSeo = "true";
      script.textContent = JSON.stringify(item);
      document.head.appendChild(script);
    });
  }, [description, image, keywords, modifiedTime, path, publishedTime, schema, title, type]);

  return null;
}
